import { Phase, Process, ProcessSpecification, Segment } from "@/types/contract";
import { Product } from "@/types/enumerations";
import { RecontractingVariant } from "@/types/recontractingRequest";
import { isOverFlowContract } from "@/utils/product";
import { TFunction } from "i18next";
import { isS3PreviousCustomer as isS3PreviousCustomerFromContract } from "@/utils/contract";
import { ProcessReasonType } from "@/types/processReason";

export const isS3PreviousCustomer = (
  process: Process,
  contractStatus: string | null | undefined,
  phase: string | null | undefined
) => {
  return isS3PreviousCustomerFromContract({ process, contractStatus, phase: phase as Phase });
};

export const getListItems = (
  selfService: boolean,
  product: Product | null,
  processSpecification: ProcessSpecification | null,
  process: Process,
  recontractingVariant: RecontractingVariant | null,
  processReason: ProcessReasonType | null,
  phase: string | null,
  segment: Segment | null,
  isContractRequest: boolean,
  isNewCustomerLink: boolean,
  t: TFunction<"welcome">
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const isSelfServiceDefined =
    process === Process.SUPPLIER_CHANGE ||
    process === Process.CHANGED_WITH_HAND_OVER ||
    process === Process.NEW_POINT ||
    (process === Process.PS && !isContractRequest);
  const isDescriptionBodyDefined =
    process === Process.VRB ||
    process === Process.DPI ||
    process === Process.UNAUTHORIZED_SP ||
    process === Process.UNAUTHORIZED ||
    process === Process.UNAUTHORIZED_PREVENTION ||
    process === Process.RECONTRACTING;
  const isVRTProcess = isOverFlowContract(product?.id);
  const isVRBProductChange = process === Process.VRB && processSpecification === ProcessSpecification.ZSP;
  const isPSRequestDeath = process === Process.PS && isContractRequest && processReason === "02";
  const isPSRequestIdeal = process === Process.PS && isContractRequest && processReason === "01";
  const isPSDraft = process === Process.PS && phase === "draft";
  const isPSContractB2BDeath =
    process === Process.PS && segment === Segment.B2B && processReason === "02" && selfService && isNewCustomerLink;
  const isPSRequestBeforeAcceptance = process === Process.PS && isNewCustomerLink;
  const isPSRequestBeforeAcceptanceDeath = isPSRequestBeforeAcceptance && processReason === "02";
  const isR1RequestNoSelfService =
    process === Process.NEW_POINT && !selfService && [Phase.REQUEST, Phase.LEAD].includes(phase ?? "");

  if (isPSDraft) {
    return t("processDescription.PSDraft", {
      returnObjects: true,
    });
  }
  if (isR1RequestNoSelfService) {
    return t("processDescription.R1RequestNoSelfService", {
      returnObjects: true,
    });
  }
  if (isSelfServiceDefined) {
    return t(`processDescription.${process}.${selfService ? "selfService" : "noSelfService"}`, {
      returnObjects: true,
    });
  }
  if (isVRTProcess) {
    return t(`processDescription.VRT`, {
      returnObjects: true,
    });
  }
  if (isVRBProductChange) {
    return t(`processDescription.VRBProductChange`, {
      returnObjects: true,
    });
  }
  if (recontractingVariant === RecontractingVariant.SAVEDESK) {
    return t(`processDescription.ZSPSavedesk`, {
      returnObjects: true,
    });
  }
  if (recontractingVariant === RecontractingVariant.OPERATORADHOC) {
    return t(`processDescription.ZSPOperatorAdhoc`, {
      returnObjects: true,
    });
  }
  if (recontractingVariant === RecontractingVariant.CUSTOMERADHOC) {
    return t(`processDescription.ZSPCustomerAdhoc`, {
      returnObjects: true,
    });
  }
  if (recontractingVariant === RecontractingVariant.ACCORD) {
    return t(`processDescription.ZSPAgreement`, {
      returnObjects: true,
    });
  }
  if (recontractingVariant === RecontractingVariant.CAMPAIGN) {
    return t(`processDescription.ZSPCampaign`, {
      returnObjects: true,
    });
  }
  if (isPSContractB2BDeath) {
    return t(`processDescription.PSselfServiceB2BDeath`, {
      returnObjects: true,
    });
  }
  if (isPSRequestBeforeAcceptanceDeath) {
    return t(`processDescription.PSRequestBeforeAcceptanceDeath`, {
      returnObjects: true,
    });
  }
  if (isPSRequestBeforeAcceptance) {
    return t(`processDescription.PSRequestBeforeAcceptance`, {
      returnObjects: true,
    });
  }
  if (process === Process.PS && !isContractRequest) {
    return t(`processDescription.PS.selfService`, {
      returnObjects: true,
    });
  }
  if (isPSRequestDeath) {
    return t(`processDescription.PSRequestDeath`, {
      returnObjects: true,
    });
  }
  if (isPSRequestIdeal) {
    return t(`processDescription.PSRequestIdeal`, {
      returnObjects: true,
    });
  }
  if (isDescriptionBodyDefined) {
    return t(`processDescription.${process}`, {
      returnObjects: true,
    });
  } else {
    return t(`processDescription.others`, { returnObjects: true });
  }
};
